import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import SEO from "@/components/seo";

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const PrivacyPolicy = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={embedded ? '' : 'min-h-screen'} style={{ background: PALETTE.brightSnow }}>
      <SEO
        title="Privacy Policy"
        description="Learn how BrandComply handles your data. Our privacy policy explains what information we collect, how we use it, and your rights as a user."
        path="/privacy-policy"
      />
      {!embedded && (
        <header style={{ background: PALETTE.gunmetal }} className="text-white sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div
                className="flex items-center space-x-1 cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                  BrandComply
                </div>
              </div>

              <Button
                variant="outline"
                className="font-semibold w-fit rounded-full border-2 h-8 px-4 hover:scale-105 transition-transform transition-colors duration-200"
                style={{ borderColor: PALETTE.inchworm, color: PALETTE.inchworm, background: 'transparent' }}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </header>
      )}

      <main className={embedded ? '' : 'max-w-4xl mx-auto px-6 py-16'}>
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
            Privacy Policy
          </h1>
          <p className="text-lg" style={{ color: `${PALETTE.gunmetal}70` }}>
            Last updated: February 24, 2026
          </p>
          <p className="text-lg mt-4" style={{ color: `${PALETTE.gunmetal}70` }}>
            This Privacy Policy explains how BrandComply (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) collects, uses, and protects your personal data in accordance with the Swiss Federal Act on Data Protection (revFADP) and the EU General Data Protection Regulation (GDPR).
          </p>
        </div>

        <div className="prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              1. Information We Collect
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Account Data:</strong> Name, email address, company name, and billing details (processed by our payment provider).
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Customer Content:</strong> Videos, audio, and brand guidelines uploaded for compliance analysis.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Usage Data:</strong> IP addresses, browser types, and interactions with our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              2. How We Use Your Information
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              We process your data based on the necessity to perform a contract with you, our legitimate business interests, or your consent, specifically to:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: `${PALETTE.gunmetal}80` }}>
              <li>Provide, operate, and maintain the BrandComply Service.</li>
              <li>Process payments and manage your subscription.</li>
              <li>Improve platform stability and user experience.</li>
              <li>Communicate updates and technical support.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              3. Data Processing and AI Sub-Processors
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              To provide our automated analysis, we utilize trusted third-party artificial intelligence providers (LLMs and VLMs).
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>No AI Training:</strong> We ensure via API agreements that your uploaded videos and brand guidelines are not used by these third-party providers to train their public models. Data is processed solely to generate your specific compliance report.
            </p>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              <strong style={{ color: PALETTE.gunmetal }}>Hosting:</strong> Our servers and primary data storage are located in the European Union / Switzerland.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              4. Data Sharing and International Transfers
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              We do not sell your data. We only share data with essential service providers (e.g., cloud hosting, payment processors) who are legally bound to protect your information. If data is transferred outside Switzerland or the EU/EEA, we ensure appropriate safeguards (such as Standard Contractual Clauses) are in place.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              5. Data Retention
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              We retain account information as long as your account is active. Uploaded videos and analysis reports can be deleted by you at any time within the platform. If you cancel your subscription, we will delete or anonymize your data in accordance with our retention policies, subject to legal archiving obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              6. Your Rights (GDPR &amp; revFADP)
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              Depending on your location, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2" style={{ color: `${PALETTE.gunmetal}80` }}>
              <li>Access the personal data we hold about you.</li>
              <li>Request correction or deletion of your data.</li>
              <li>Object to or restrict the processing of your data.</li>
              <li>Request data portability.</li>
            </ul>
            <p className="text-base leading-relaxed mt-4 mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              To exercise these rights, or to delete your account entirely, please contact us at the email below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              7. Security
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              We implement robust technical and organizational measures to secure your data against unauthorized access. However, no internet transmission is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              8. Changes to this Policy
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              We may update this Privacy Policy as our Service evolves. We will notify you of any significant changes via email or an in-app notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
              9. Contact Us
            </h2>
            <p className="text-base leading-relaxed mb-4" style={{ color: `${PALETTE.gunmetal}80` }}>
              If you have any questions about this Privacy Policy or how we handle your data, please contact us at:{' '}
              <a href="mailto:info@brandcomply.ai" className="underline hover:no-underline" style={{ color: PALETTE.gunmetal }}>
                info@brandcomply.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      {!embedded && (
        <footer className="py-12 text-white" style={{ background: PALETTE.gunmetal }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-16">
              <div className="max-w-xs">
                <div
                  className="flex items-center space-x-1 cursor-pointer"
                  onClick={() => navigate('/')}
                >
                  <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                  <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                    BrandComply
                  </div>
                </div>
                <p className="text-white/70 mt-4">
                  Launch influencer & UGC video campaigns faster and on-brand.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>Product</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/')} className="block text-white/70 hover:text-white transition-colors text-left">How it Works</button>
                  <button onClick={() => navigate('/')} className="block text-white/70 hover:text-white transition-colors text-left">Pricing</button>
                  <button onClick={() => navigate('/api')} className="block text-white/70 hover:text-white transition-colors text-left">API</button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>Legal</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate('/terms-of-service')} className="block text-white/70 hover:text-white transition-colors text-left">Terms of Service</button>
                  <button onClick={() => navigate('/privacy-policy')} className="block text-white/70 hover:text-white transition-colors text-left">Privacy Policy</button>
                  <button onClick={() => navigate('/imprint')} className="block text-white/70 hover:text-white transition-colors text-left">Imprint</button>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
              <p>&copy; 2026 BrandComply. All rights reserved.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default PrivacyPolicy;
